/**
 * Discount Types
 */

import { DateTime } from "luxon";

export type DiscountApplyTo = "per_item" | "total";

export type DiscountValueType = "percentage" | "fixed";

export type DiscountTargetType = "all_services" | "specific_services";

export type CouponType = "public" | "private";

export interface TimeSlot {
  id: string;
  startTime: string; // HH:mm format
  endTime: string; // HH:mm format
}

export interface DaySchedule {
  dayOfWeek: number; // 0-6 (Sunday-Saturday)
  dayName: string;
  timeSlots: TimeSlot[];
}

export interface DiscountRuleFormData {
  valueType: DiscountValueType;
  value: number; // percentage (0-100) or fixed amount in cents
  targetType: DiscountTargetType;
  selectedServiceIds: string[];
}

export interface DiscountFormData {
  // Method — replaces old DiscountMethod enum
  autoApply: boolean;

  // Title
  title: string;

  // Coupon section (only when !autoApply)
  couponType: CouponType;
  code?: string;

  // Apply to: per_item or total
  applyTo: DiscountApplyTo;

  // Rules array (replaces flat valueType/value/appliesTo)
  rules: DiscountRuleFormData[];

  // Minimum spent requirement (in smallest currency unit, e.g., cents)
  minSpent?: number;

  // Active period section - using DateTime for proper timezone handling
  startDateTime: DateTime;
  endDateTime: DateTime;

  // Targeted period section (optional) - can have both time interval AND off-peak hours
  hasTimeInterval: boolean;
  targetStartDateTime?: DateTime;
  targetEndDateTime?: DateTime;

  hasOffPeakHours: boolean;
  offPeakHours: DaySchedule[];

  // UI-only flag for visibility period advanced condition
  hasVisibilityPeriod?: boolean;

  // Active status
  isActive?: boolean;
}

export interface Discount extends DiscountFormData {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
}

// Default time slots for initializing off-peak hours
export const createDefaultTimeSlot = (
  startTime = "09:00",
  endTime = "17:00",
  index = 0
): TimeSlot => ({
  id: `slot-${index}-${Date.now()}`,
  startTime,
  endTime,
});

// Default day schedule for initializing off-peak hours
export const createDefaultDaySchedules = (): DaySchedule[] => {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  return days.map((dayName, index) => ({
    dayOfWeek: index,
    dayName,
    timeSlots: [], // Start with no time slots (closed)
  }));
};

export const createDefaultRule = (): DiscountRuleFormData => ({
  valueType: "percentage",
  value: 0,
  targetType: "all_services",
  selectedServiceIds: [],
});

// Default form values
export const getDefaultDiscountFormData = (): DiscountFormData => {
  const now = DateTime.now();
  return {
    autoApply: true,
    title: "",
    couponType: "public",
    applyTo: "per_item",
    rules: [createDefaultRule()],
    startDateTime: now.startOf("day"),
    endDateTime: now.endOf("day"),
    hasTimeInterval: false,
    targetStartDateTime: now.startOf("day"),
    targetEndDateTime: now.endOf("day"),
    hasOffPeakHours: false,
    offPeakHours: createDefaultDaySchedules(),
    hasVisibilityPeriod: false,
  };
};
